import React, { useRef } from 'react';
import { KPICard } from '../ui';
import { useHRPerformanceDistribution } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface PersonnelPerformanceDistributionProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const PersonnelPerformanceDistribution: React.FC<PersonnelPerformanceDistributionProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useHRPerformanceDistribution(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'personnel-performance-distribution';
  const componentTitle = 'توزیع عملکرد پرسنل';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری توزیع عملکرد پرسنل</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="kpi"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.personnelPerformanceDistribution)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'kpi',
            description: 'توزیع عملکرد پرسنل بر اساس سطح عملکرد',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">{componentTitle}</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <KPICard
          title="عملکرد عالی"
          value={data.topPerformers}
          variant="success"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد پرسنل با عملکرد عالی: ${data.topPerformers}`}
        />
        
        <KPICard
          title="عملکرد خوب"
          value={data.goodPerformers}
          variant="warning"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد پرسنل با عملکرد خوب: ${data.goodPerformers}`}
        />
        
        <KPICard
          title="نیاز به بهبود"
          value={data.needsImprovement}
          variant="error"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد پرسنل با عملکرد نیازمند بهبود: ${data.needsImprovement}`}
        />
      </div>
    </div>
  );
};

export default PersonnelPerformanceDistribution;